import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { usePrinter } from '../../../contexts/printer-context';
import { PrinterSelector } from '../printer-selector';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import {
  stringToArrayBuffer,
  numberArrayToArrayBuffer,
  sendToPrinterInChunks,
} from '../../../hooks/use-web-bluetooth';

enum OrderPrintingState {
  PENDING = 'pending',
  GENERATING = 'generating',
  PRINTING = 'printing',
  PRINTED = 'printed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

interface ThermalPrintSectionProps {
  orders: OrderType[];
}

const CHUNK_SIZE = 10; // Process 10 orders at a time

export const ThermalPrintSection: React.FC<ThermalPrintSectionProps> = ({ orders }) => {
  const { selectedPrinter, isConnected, getConnectedCharacteristic } = usePrinter();
  const { showMessage } = useSnackBar();
  const { config } = React.useContext(AuthContext);

  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(
    new Set(orders.map((o) => String(o.id)))
  );
  const [orderStates, setOrderStates] = useState<Map<string, OrderPrintingState>>(
    new Map(orders.map((o) => [String(o.id), OrderPrintingState.PENDING]))
  );

  const sortedOrders = useMemo(
    () => orders.sort((a, b) => (parseInt(a.order_id) < parseInt(b.order_id) ? -1 : 1)),
    [orders]
  );

  const selectedOrders = useMemo(
    () => sortedOrders.filter((o) => selectedOrderIds.has(String(o.id))),
    [sortedOrders, selectedOrderIds]
  );

  const setOrdersStates = (orderIds: string[], state: OrderPrintingState) => {
    setOrderStates((prev) => {
      const newMap = new Map(prev);
      orderIds.forEach((id) => newMap.set(id, state));
      return newMap;
    });
  };

  const toggleOrderSelection = (orderId: string) => {
    if (isPrinting) return; // Don't allow changes while printing
    
    setSelectedOrderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (isPrinting) return;
    setSelectedOrderIds(new Set(orders.map((o) => String(o.id))));
  };

  const deselectAll = () => {
    if (isPrinting) return;
    setSelectedOrderIds(new Set());
  };

  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  const handlePrint = async () => {
    if (!selectedPrinter) {
      showMessage('Please select a printer first', 'error');
      return;
    }

    if (selectedOrders.length === 0) {
      showMessage('Please select at least one order to print', 'warning');
      return;
    }

    // Mark unselected orders as skipped
    const unselectedIds = orders
      .filter((o) => !selectedOrderIds.has(String(o.id)))
      .map((o) => String(o.id));
    setOrdersStates(unselectedIds, OrderPrintingState.SKIPPED);

    // Get fresh connection and characteristic
    console.log('[ThermalPrint] Getting fresh connection...');
    const freshCharacteristic = await getConnectedCharacteristic();
    if (!freshCharacteristic) {
      showMessage('Failed to connect to printer', 'error');
      return;
    }
    console.log('[ThermalPrint] Got fresh characteristic:', freshCharacteristic.uuid);

    setIsPrinting(true);

    // Split selected orders into chunks
    const orderGroups = chunkArray(selectedOrders, CHUNK_SIZE);

    for (const orderGroup of orderGroups) {
      try {
        // Mark as generating
        setOrdersStates(
          orderGroup.map((o) => String(o.id)),
          OrderPrintingState.GENERATING
        );

        // Fetch bitmap data from backend
        console.log('[ThermalPrint] Fetching bitmaps for orders:', orderGroup.map((o) => o.order_id));
        const response = await axios.post(
          `${API_ENDPOINT}/api/v1/orders/bitmaps/`,
          {
            order_ids: orderGroup.map((o) => o.id),
            name: selectedPrinter.name,
          },
          config
        );

        const { pages, preCommands, postCommands, bitmapX, bitmapY, widthInBytes, heightInDots } =
          response.data;
        
        console.log('[ThermalPrint] Received data:', {
          pageCount: pages.length,
          preCommands,
          postCommands,
          bitmapX,
          bitmapY,
          widthInBytes,
          heightInDots,
          firstPageSize: pages[0]?.length,
        });

        // Mark as printing
        setOrdersStates(
          orderGroup.map((o) => String(o.id)),
          OrderPrintingState.PRINTING
        );

        // Print each page
        for (let i = 0; i < pages.length; i++) {
          console.log(`[ThermalPrint] Printing page ${i + 1}/${pages.length}`);
          const data: number[] = pages[i];

          // Construct print commands
          const commands: ArrayBuffer[] = [];

          // Add pre-commands
          for (const cmd of preCommands) {
            commands.push(stringToArrayBuffer(cmd));
          }

          // Add bitmap command
          const bitmapCmd = `BITMAP ${bitmapX},${bitmapY},${widthInBytes},${heightInDots},0,`;
          commands.push(stringToArrayBuffer(bitmapCmd));

          // Add bitmap data
          commands.push(numberArrayToArrayBuffer(data));

          // Add newline
          commands.push(stringToArrayBuffer('\n'));

          // Add post-commands
          for (const cmd of postCommands) {
            commands.push(stringToArrayBuffer(cmd));
          }

          // Combine all commands into a single buffer
          const totalLength = commands.reduce((sum, buf) => sum + buf.byteLength, 0);
          const combinedBuffer = new ArrayBuffer(totalLength);
          const combinedView = new Uint8Array(combinedBuffer);
          let offset = 0;

          for (const buf of commands) {
            combinedView.set(new Uint8Array(buf), offset);
            offset += buf.byteLength;
          }

          console.log(`[ThermalPrint] Sending ${totalLength} bytes to printer`);
          
          // Write to printer in chunks using fresh characteristic
          const success = await sendToPrinterInChunks(
            freshCharacteristic,
            new Uint8Array(combinedBuffer)
            // Uses default chunk size (200) and delay (10ms)
          );

          if (!success) {
            throw new Error('Failed to write to printer');
          }

          console.log(`[ThermalPrint] ✓ Page ${i + 1} sent successfully`);

          // Reduced delay between pages for faster printing
          if (i < pages.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Mark as printed
        setOrdersStates(
          orderGroup.map((o) => String(o.id)),
          OrderPrintingState.PRINTED
        );
      } catch (error: any) {
        console.error('Error printing orders:', error);
        setOrdersStates(
          orderGroup.map((o) => String(o.id)),
          OrderPrintingState.FAILED
        );
        showMessage(`Failed to print some orders: ${error.message}`, 'error');
      }
    }

    setIsPrinting(false);
    showMessage('Printing completed', 'success');
  };

  const getStateIcon = (state: OrderPrintingState) => {
    switch (state) {
      case OrderPrintingState.PENDING:
        return <PendingIcon fontSize="small" />;
      case OrderPrintingState.GENERATING:
      case OrderPrintingState.PRINTING:
        return <CircularProgress size={16} />;
      case OrderPrintingState.PRINTED:
        return <CheckCircleIcon fontSize="small" />;
      case OrderPrintingState.FAILED:
        return <ErrorIcon fontSize="small" />;
      case OrderPrintingState.SKIPPED:
        return null;
    }
  };

  const getStateLabel = (state: OrderPrintingState) => {
    switch (state) {
      case OrderPrintingState.PENDING:
        return 'Pending';
      case OrderPrintingState.GENERATING:
        return 'Preparing...';
      case OrderPrintingState.PRINTING:
        return 'Printing...';
      case OrderPrintingState.PRINTED:
        return 'Done';
      case OrderPrintingState.FAILED:
        return 'Failed';
      case OrderPrintingState.SKIPPED:
        return 'Skipped';
    }
  };

  const getStateColor = (state: OrderPrintingState): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (state) {
      case OrderPrintingState.PENDING:
        return 'default';
      case OrderPrintingState.GENERATING:
      case OrderPrintingState.PRINTING:
        return 'info';
      case OrderPrintingState.PRINTED:
        return 'success';
      case OrderPrintingState.FAILED:
        return 'error';
      case OrderPrintingState.SKIPPED:
        return 'default';
    }
  };

  const pendingCount = Array.from(orderStates.values()).filter(
    (s) => s === OrderPrintingState.PENDING
  ).length;
  const printingCount = Array.from(orderStates.values()).filter(
    (s) => s === OrderPrintingState.GENERATING || s === OrderPrintingState.PRINTING
  ).length;
  const printedCount = Array.from(orderStates.values()).filter(
    (s) => s === OrderPrintingState.PRINTED
  ).length;
  const failedCount = Array.from(orderStates.values()).filter(
    (s) => s === OrderPrintingState.FAILED
  ).length;

  const progress = selectedOrders.length > 0 ? (printedCount / selectedOrders.length) * 100 : 0;

  return (
    <Box>
      <PrinterSelector />

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connect to a printer above to start printing
          </Typography>
        </Alert>
      )}

      {isConnected && !isPrinting && printedCount === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {selectedOrderIds.size} of {orders.length} {orders.length === 1 ? 'order' : 'orders'} selected for printing
          </Typography>
        </Alert>
      )}

      {(isPrinting || printedCount > 0) && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Progress
            </Typography>
            <Typography variant="body2" fontWeight={600} color="primary">
              {printedCount} / {selectedOrders.length}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, height: 8, borderRadius: 4 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {printedCount > 0 && (
              <Chip
                icon={<CheckCircleIcon />}
                label={`${printedCount} Printed`}
                size="small"
                color="success"
              />
            )}
            {printingCount > 0 && (
              <Chip
                icon={<CircularProgress size={16} />}
                label={`${printingCount} Printing`}
                size="small"
                color="info"
              />
            )}
            {failedCount > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${failedCount} Failed`}
                size="small"
                color="error"
              />
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={isPrinting ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={!isConnected || isPrinting || selectedOrderIds.size === 0}
          sx={{ flex: 1 }}
        >
          {isPrinting ? 'Printing...' : `Print ${selectedOrderIds.size} ${selectedOrderIds.size === 1 ? 'Order' : 'Orders'}`}
        </Button>
        
        {!isPrinting && (
          <>
            <Tooltip title="Select All">
              <IconButton onClick={selectAll} size="small" color="primary">
                <SelectAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deselect All">
              <IconButton onClick={deselectAll} size="small" color="default">
                <DeselectIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1 }}>
        {sortedOrders.map((order) => {
          const state = orderStates.get(String(order.id)) || OrderPrintingState.PENDING;
          const isSelected = selectedOrderIds.has(String(order.id));

          return (
            <ListItem
              key={order.id}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 },
                opacity: state === OrderPrintingState.SKIPPED ? 0.5 : 1,
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStateIcon(state)}
                  <Chip
                    label={getStateLabel(state)}
                    size="small"
                    color={getStateColor(state)}
                    variant={state === OrderPrintingState.PENDING ? 'outlined' : 'filled'}
                  />
                </Box>
              }
            >
              <Checkbox
                checked={isSelected}
                onChange={() => toggleOrderSelection(String(order.id))}
                disabled={isPrinting}
                sx={{ mr: 1 }}
              />
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    Order #{order.order_id}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {order.customer?.name || 'No customer'}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
